// Stage 2 "Dashboard" · 2.3 (d) — Staff time (Partner / Manager). SUNDAY BATCH D-5: the locked matrix — one row per staff member
// (with grade) × one column per engagement × the row total, plus a column-total row and the grand total. A cell opens that
// person's entries on that engagement (`/billing?user=<id>`, engagement selected); a row total opens the person's entries; a
// column total opens the engagement's Billing page. Columns = the engagements in scope that carry time (an engagement with no
// time recorded has no column). Many columns: fixed widths, the matrix scrolls sideways INSIDE the panel only (the staff
// column stays in place), the page itself never scrolls sideways. Hours through fdTheme.fmtSeconds (D-6). The Preparer has no
// matrix (My time only). Built from the API's flat `staff_time` rows (one source: backend firm_dashboard._staff_time).
import React from "react";
import { FD, TARGET, fmtSeconds } from "./fdTheme";
import { FdLink, Panel, Td, Empty } from "./FdPrimitives";
import { FdResizableTable, FdRTh } from "./FdResizable";   // Step 11d · add-on: adjustable columns
import { useResizableColumns } from "@/components/ui/resizable-table";

export const STAFF_COL_PX = 200;
export const ENG_COL_PX = 112;
export const TOTAL_COL_PX = 96;
const STICKY = { position: "sticky", insetInlineStart: 0, zIndex: 1 };

export function staffMatrix(rows, engagements) {
  const list = Array.isArray(rows) ? rows : [];
  const meta = {};
  for (const e of Array.isArray(engagements) ? engagements : []) if (e && e.engagement_id) meta[e.engagement_id] = e;
  const cols = []; const colSeen = new Set(); const people = new Map(); const colTotal = {};
  let grand = 0;
  for (const r of list) {
    const secs = Number(r.seconds || 0);
    if (!colSeen.has(r.engagement_id)) {
      colSeen.add(r.engagement_id);
      const m = meta[r.engagement_id] || {};
      cols.push({ id: r.engagement_id, client: r.client_name || m.client_name || "", fy: m.financial_year || "" });
    }
    if (!people.has(r.user_id)) people.set(r.user_id, { user_id: r.user_id, name: r.name || "", grade: r.grade || "", cells: {}, total: 0 });
    const p = people.get(r.user_id);
    p.cells[r.engagement_id] = (p.cells[r.engagement_id] || 0) + secs;
    p.total += secs;
    colTotal[r.engagement_id] = (colTotal[r.engagement_id] || 0) + secs;
    grand += secs;
  }
  cols.sort((a, b) => a.client.localeCompare(b.client) || String(a.fy).localeCompare(String(b.fy)));
  const staff = [...people.values()].sort((a, b) => a.name.localeCompare(b.name));
  return { cols, staff, colTotal, grand };
}

export default function FdStaffTime({ rows, engagements, t, lang }) {
  const { cols, staff, colTotal, grand } = staffMatrix(rows, engagements);
  const h = (s) => fmtSeconds(s, lang, t, { unit: false });
  // Step 11d · add-on: the staff column, one column per engagement, the total — adjustable (one key per column count)
  const stCols = useResizableColumns({ storageKey: `dashboard-staff-time-${cols.length}`,
    defaults: [STAFF_COL_PX, ...cols.map(() => ENG_COL_PX), TOTAL_COL_PX], mins: [140, ...cols.map(() => 80), 80] });
  const tot = { background: FD.thBg, borderTop: `2px solid ${FD.thLine}` };
  return (
    <Panel title={t("fd.staff.title")} testid="fd-staff-time" hint={cols.length > 8 ? t("fd.staff.scrollHint") : undefined}>
      {staff.length === 0 ? <Empty>{t("fd.staff.empty")}</Empty> : (
        <div data-testid="fd-staff-scroll" style={{ overflowX: "auto", maxWidth: "100%" }}>
          <FdResizableTable cols={stCols} testid="fd-staff-table" className="" scroll={false}>
            <thead>
              <tr>
                <FdRTh index={0} style={STICKY}>{t("fd.staff.staff")}</FdRTh>
                {cols.map((c, i) => (
                  <FdRTh key={c.id} index={i + 1} align="end" className="normal-case tracking-normal">
                    <span data-testid={`fd-staff-col-${c.id}`}>{c.client}</span>
                    {c.fy ? <span className="block text-[10px] font-normal" style={{ color: FD.muted }}>{c.fy}</span> : null}
                  </FdRTh>
                ))}
                <FdRTh index={cols.length + 1} align="end">{t("fd.staff.total")}</FdRTh>
              </tr>
            </thead>
            <tbody>
              {staff.map((p) => (
                <tr key={p.user_id} data-testid={`fd-staff-row-${p.user_id}`}>
                  <Td style={STICKY}>
                    <FdLink to={TARGET.billingUser(p.user_id)} testid={`fd-staff-${p.user_id}`} className="font-medium">
                      {p.name}
                      <span className="block text-xs font-normal" style={{ color: FD.muted }}>{p.grade || "—"}</span>
                    </FdLink>
                  </Td>
                  {cols.map((c) => (
                    <Td key={c.id} align="end" className="tabular-nums">
                      {p.cells[c.id] !== undefined ? (
                        <FdLink to={TARGET.billingUser(p.user_id)} engId={c.id} testid={`fd-staff-cell-${p.user_id}-${c.id}`}>{h(p.cells[c.id])}</FdLink>
                      ) : <span style={{ color: FD.grey }}>·</span>}
                    </Td>
                  ))}
                  <Td align="end" className="font-semibold tabular-nums">
                    <FdLink to={TARGET.billingUser(p.user_id)} testid={`fd-staff-rowtotal-${p.user_id}`}>{h(p.total)}</FdLink>
                  </Td>
                </tr>
              ))}
              <tr data-testid="fd-staff-total-row">
                <Td className="font-semibold" style={{ ...tot, ...STICKY }}>{t("fd.staff.total")}</Td>
                {cols.map((c) => (
                  <Td key={c.id} align="end" className="font-semibold tabular-nums" style={tot}>
                    <FdLink to={TARGET.billing} engId={c.id} testid={`fd-staff-coltotal-${c.id}`}>{h(colTotal[c.id])}</FdLink>
                  </Td>
                ))}
                <Td align="end" className="font-semibold tabular-nums" style={tot}><span data-testid="fd-staff-grand">{h(grand)}</span></Td>
              </tr>
            </tbody>
          </FdResizableTable>
        </div>
      )}
    </Panel>
  );
}
